import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Box, Typography, Grid, Button, TextField,
  Chip, FormControl, InputLabel, Select, MenuItem, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Card, CardContent, IconButton, Tooltip,
  Alert, Snackbar
} from '@mui/material';
import {
  CalendarMonth, FilterList, Refresh, Visibility,
  DirectionsCar, Person, AttachMoney, Schedule,
  Close, CheckCircle, Cancel, PendingActions, HourglassEmpty
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';

export default function CalendarioReservas() {
  const calendarRef = useRef(null);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroVeiculo, setFiltroVeiculo] = useState('todos');
  const [veiculos, setVeiculos] = useState([]);
  
  // Modal de detalhes
  const [modalAberto, setModalAberto] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cores por status (mais profissionais com melhor contraste)
  const coresStatus = {
    'Reservado': '#FFA726', // Laranja vibrante
    'Agendado': '#FFA726',  // Laranja vibrante
    'Ativo': '#66BB6A',      // Verde profissional
    'Em Andamento': '#66BB6A', // Verde profissional
    'Finalizado': '#42A5F5',  // Azul profissional
    'Cancelado': '#EF5350',   // Vermelho profissional
    'Pendente': '#FFCA28'     // Amarelo/Dourado
  };

  // Ícones por status
  const iconesStatus = {
    'Reservado': <HourglassEmpty />,
    'Agendado': <HourglassEmpty />,
    'Ativo': <CheckCircle />,
    'Em Andamento': <CheckCircle />,
    'Finalizado': <CheckCircle />,
    'Cancelado': <Cancel />,
    'Pendente': <PendingActions />
  };

  // Buscar veículos para o filtro
  useEffect(() => {
    buscarVeiculos();
  }, []);

  // Buscar alugueis ao carregar e quando filtros mudarem
  useEffect(() => {
    buscarReservas();
  }, [filtroStatus, filtroVeiculo]);

  const buscarVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/veiculos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVeiculos(data);
      }
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
    }
  };

  const buscarReservas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Se o usuário filtrou por veículo, usar rota dedicada que retorna { alugueis: [...] }
      let response;
      if (filtroVeiculo !== 'todos') {
        response = await fetch(`${process.env.REACT_APP_API_URL}/alugueis/veiculo/${filtroVeiculo}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const payload = await response.json();
          // endpoint retorna { alugueis: [...] }
          formatarEventos(payload.alugueis || []);
        } else {
          mostrarSnackbar('Erro ao buscar reservas por veículo', 'error');
        }
      } else if (filtroStatus !== 'todos') {
        // Usar rota de relatórios para filtrar por status_aluguel
        const params = new URLSearchParams();
        params.append('status_aluguel', filtroStatus);
        response = await fetch(`${process.env.REACT_APP_API_URL}/alugueis/relatorios?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          formatarEventos(data);
        } else {
          mostrarSnackbar('Erro ao buscar reservas por status', 'error');
        }
      } else {
        // Lista padrão
        response = await fetch(`${process.env.REACT_APP_API_URL}/alugueis`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          formatarEventos(data);
        } else {
          mostrarSnackbar('Erro ao buscar reservas', 'error');
        }
      }
    } catch (error) {
      console.error('Erro ao buscar reservas:', error);
      mostrarSnackbar('Erro ao buscar reservas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatarEventos = (alugueis) => {
    const eventosFormatados = alugueis.map(aluguel => {
      const cor = coresStatus[aluguel.status_aluguel] || '#757575';

      // Backend retorna campos como nome_cliente, modelo, placa
      const clienteNome = aluguel.nome_cliente || aluguel.cliente_nome || aluguel.clienteNome || '';
      const veiculoModelo = aluguel.modelo || aluguel.veiculo_modelo || aluguel.veiculoModelo || '';
      const veiculoPlaca = aluguel.placa || aluguel.veiculo_placa || aluguel.veiculoPlaca || '';

      const titulo = `🚗 ${veiculoModelo} | ${clienteNome}`;

      return {
        id: aluguel.id,
        title: titulo,
        start: aluguel.data_retirada || aluguel.dataRetirada,
        end: aluguel.data_devolucao || aluguel.dataDevolucao,
        allDay: true,
        backgroundColor: cor,
        borderColor: cor,
        textColor: '#FFFFFF',
        display: 'block',
        extendedProps: {
          clienteNome,
          clienteTelefone: aluguel.telefone || aluguel.cliente_telefone || aluguel.clienteTelefone || '',
          veiculoModelo,
          veiculoPlaca,
          valorTotal: aluguel.valor_total || aluguel.valorTotal || aluguel.valor_bruto || null,
          valorDiaria: aluguel.valor_diaria || aluguel.valorDiaria || null,
          status: aluguel.status_aluguel || aluguel.status || '',
          statusPagamento: aluguel.status_pagamento || aluguel.statusPagamento || '',
          diasTotal: aluguel.dias_total || aluguel.diasTotal || null,
          observacoes: aluguel.observacoes || aluguel.observacao || '',
          dataRetirada: aluguel.data_retirada || aluguel.dataRetirada,
          dataDevolucao: aluguel.data_devolucao || aluguel.dataDevolucao
        }
      };
    });

    setEventos(eventosFormatados);
  };

  const handleEventClick = (info) => {
    const { event } = info;
    setReservaSelecionada({
      id: event.id,
      title: event.title,
      ...event.extendedProps
    });
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setReservaSelecionada(null);
  };

  const mostrarSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fecharSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const limparFiltros = () => {
    setFiltroStatus('todos');
    setFiltroVeiculo('todos');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CalendarMonth sx={{ fontSize: 40, color: '#FF8C00' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Calendário de Reservas
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Visualize todas as reservas e aluguéis em formato de calendário
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filtroStatus}
                label="Status"
                onChange={(e) => setFiltroStatus(e.target.value)}
              >
                <MenuItem value="todos">Todos os Status</MenuItem>
                <MenuItem value="Reservado">Reservado</MenuItem>
                <MenuItem value="Agendado">Agendado</MenuItem>
                <MenuItem value="Ativo">Ativo</MenuItem>
                <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                <MenuItem value="Finalizado">Finalizado</MenuItem>
                <MenuItem value="Cancelado">Cancelado</MenuItem>
                <MenuItem value="Pendente">Pendente</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Veículo</InputLabel>
              <Select
                value={filtroVeiculo}
                label="Veículo"
                onChange={(e) => setFiltroVeiculo(e.target.value)}
              >
                <MenuItem value="todos">Todos os Veículos</MenuItem>
                {veiculos.map(veiculo => (
                  <MenuItem key={veiculo.id} value={veiculo.id}>
                    {veiculo.modelo} - {veiculo.placa}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={buscarReservas}
              fullWidth
            >
              Atualizar
            </Button>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={limparFiltros}
              fullWidth
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>

        {/* Legenda de Cores */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Legenda de Status:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip 
              label="Reservado/Agendado" 
              size="small" 
              sx={{ backgroundColor: '#FFA726', color: 'white', fontWeight: 'bold' }} 
            />
            <Chip 
              label="Ativo/Em Andamento" 
              size="small" 
              sx={{ backgroundColor: '#66BB6A', color: 'white', fontWeight: 'bold' }} 
            />
            <Chip 
              label="Finalizado" 
              size="small" 
              sx={{ backgroundColor: '#42A5F5', color: 'white', fontWeight: 'bold' }} 
            />
            <Chip 
              label="Cancelado" 
              size="small" 
              sx={{ backgroundColor: '#EF5350', color: 'white', fontWeight: 'bold' }} 
            />
            <Chip 
              label="Pendente" 
              size="small" 
              sx={{ backgroundColor: '#FFCA28', color: '#000', fontWeight: 'bold' }} 
            />
          </Stack>
        </Box>
      </Paper>

      {/* Calendário */}
      <Paper sx={{ p: 3 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,listMonth'
          }}
          buttonText={{
            today: 'Hoje',
            month: 'Mês',
            list: 'Lista'
          }}
          locale={ptBrLocale}
          events={eventos}
          eventClick={handleEventClick}
          height="auto"
          eventDisplay="block"
          displayEventTime={false}
          displayEventEnd={false}
          expandRows={true}
          nowIndicator={true}
          loading={setLoading}
          dayMaxEvents={3}
          moreLinkText="mais"
          eventContent={(eventInfo) => (
            <Box
              sx={{
                p: 0.5,
                fontSize: '0.8rem',
                fontWeight: '700',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 }
              }}
            >
              {eventInfo.event.title}
            </Box>
          )}
        />
      </Paper>

      {/* Modal de Detalhes */}
      <Dialog
        open={modalAberto}
        onClose={fecharModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detalhes da Reserva</Typography>
            <IconButton onClick={fecharModal} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        
        <DialogContent dividers>
          {reservaSelecionada && (
            <Grid container spacing={3}>
              {/* Status */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={iconesStatus[reservaSelecionada.status]}
                    label={reservaSelecionada.status}
                    sx={{
                      backgroundColor: coresStatus[reservaSelecionada.status],
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label={`Pagamento: ${reservaSelecionada.statusPagamento || 'Pendente'}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <Person color="primary" />
                      <Typography variant="h6">Cliente</Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Nome:</strong> {reservaSelecionada.clienteNome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Telefone:</strong> {reservaSelecionada.clienteTelefone || 'Não informado'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Veículo */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <DirectionsCar color="primary" />
                      <Typography variant="h6">Veículo</Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Modelo:</strong> {reservaSelecionada.veiculoModelo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Placa:</strong> {reservaSelecionada.veiculoPlaca}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Datas */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <Schedule color="primary" />
                      <Typography variant="h6">Período</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Retirada:</strong> {formatarData(reservaSelecionada.dataRetirada)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Devolução:</strong> {formatarData(reservaSelecionada.dataDevolucao)}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      <strong>Total:</strong> {reservaSelecionada.diasTotal} dias
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Valores */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <AttachMoney color="primary" />
                      <Typography variant="h6">Valores</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Diária:</strong> {formatarValor(reservaSelecionada.valorDiaria)}
                    </Typography>
                    <Typography variant="h5" color="success.main" sx={{ mt: 2 }}>
                      <strong>Total:</strong> {formatarValor(reservaSelecionada.valorTotal)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Observações */}
              {reservaSelecionada.observacoes && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>Observações</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reservaSelecionada.observacoes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={fecharModal}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={<Visibility />}
            onClick={() => {
              fecharModal();
              // Redireciona para a visualização do aluguel (rota /alugueis/:id)
              window.location.href = `/alugueis/${reservaSelecionada?.id}`;
            }}
          >
            Ver Detalhes Completos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={fecharSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
